/**
 * Порти синхронізації.
 *
 * Тут лише те, що синхронізація потребує **ззовні**: сховище власного
 * стану, транспорт до сервера, черга вихідних. Правила злиття портами
 * не є — вони чисті функції й живуть у `crypto`-сусідстві, в `sync/`.
 */
/**
 * Імена ключів зібрані в одному місці, бо рядок, набраний із помилкою
 * у другому місці, дає не помилку, а мовчазну втрату стану: курсор
 * зчитується як порожній, і застосунок качає архів спочатку.
 */
export const SYNC_KEYS = {
    /** Останній серверний `seq`, який ми застосували. */
    cursor: "sync.cursor",
    /** Стан HLC-годинника між запусками: `${millis}.${counter}`. */
    hlcWatermark: "sync.hlcWatermark",
    /** Ідентифікатор цього пристрою **на сервері** — щоб не застосовувати власні дельти. */
    serverDeviceId: "sync.serverDeviceId",
    /** Докіль дійшов перший прогін, ідучи від найновішого назад. */
    backfillEntry: "sync.backfill.entry",
    backfillItem: "sync.backfill.item",
    /*
     * Люди — три окремі курсори, бо це три таблиці.
     *
     * Порядок між ними не довільний: дати й канали починаються лише після
     * того, як картки дійшли кінця. Дата, що приїхала раніше за свою
     * картку, впирається в зовнішній ключ і лягає в карантин.
     */
    backfillPerson: "sync.backfill.person",
    backfillPersonDate: "sync.backfill.personDate",
    backfillPersonChannel: "sync.backfill.personChannel",
    /** `"1"`, коли перший прогін дійшов до кінця. Курсор до нуля не спадає. */
    backfillDone: "sync.backfill.done",
    /**
     * Акаунт, під ключем якого лежить архів **у базі**.
     *
     * Єдиний спосіб дізнатися, з якого боку обриву опинилося
     * перешифрування на чужий ключ ([ADR-0026]): він пишеться тим самим
     * `COMMIT`, що й самі дані, а обгортки живуть в іншому сховищі й
     * атомарними з ним бути не можуть.
     */
    accountId: "sync.accountId",
};
/**
 * Помилки синхронізації розділені за **реакцією**, а не за кодом HTTP.
 *
 * Той, хто ловить, має вирішити одне: пробувати ще чи спинитися й
 * сказати людині. Код відповіді на це питання не відповідає — 401
 * і 503 обидва «не вийшло», але перший означає, що повтори марні
 * назавжди, а другий — що марні наступні кілька секунд.
 */
export class SyncError extends Error {
}
/** Мережі немає. Найзвичайніший стан застосунку, який обіцяє офлайн. */
export class SyncOfflineError extends SyncError {
    retryable = true;
    constructor(reason) {
        super(`сервер недосяжний: ${reason}`);
        this.name = "SyncOfflineError";
    }
}
/** Сервер відповів, але собою незадоволений. Мине саме. */
export class SyncServerError extends SyncError {
    retryable = true;
    status;
    constructor(status, reason) {
        super(`сервер відповів ${status}: ${reason}`);
        this.name = "SyncServerError";
        this.status = status;
    }
}
/** Токен протух або відкликаний. Повтори не допоможуть — потрібен пароль. */
export class SyncAuthError extends SyncError {
    retryable = false;
    constructor(reason) {
        super(`потрібен вхід: ${reason}`);
        this.name = "SyncAuthError";
    }
}
/**
 * Сервер відхилив саме цю пачку і відхилятиме завжди: завелика дельта,
 * зіпсована форма, чужий ідентифікатор. Повторювати — це вічний цикл
 * із тим самим курсором, тому такі йдуть у карантин.
 */
export class SyncRejectedError extends SyncError {
    retryable = false;
    constructor(reason) {
        super(`сервер відхилив дельту: ${reason}`);
        this.name = "SyncRejectedError";
    }
}
//# sourceMappingURL=sync.js.map